import { useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Building2, ChevronsUpDown, MapPin, Search } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import {
  CustomerSelector,
  type CustomerSelectorContactResult,
  type CustomerSelectorSelectedContact,
} from './CustomerSelector';
import { useContactChildren } from '@/hooks/contacts/useContactRelationships';
import {
  formatContactAddress,
  getContactDisplayName,
  getPrimaryContactAddress,
} from '@/lib/contacts';
import type { Contact } from '@/types/contact';
import { getCommercialPrimaryContact } from '@/lib/contacts/commercialContactPerson';
import { needsLocationSelection } from './CompanyLocationSelector.logic';

/**
 * ServiceTitan-style two-field customer picker: search a company, then pick the
 * Location (a branch — a child contact linked via `parentContactId`) or the main
 * account. It is a drop-in for {@link CustomerSelector} (same props, same
 * `onSelect` payload) so the estimate/job customer sections keep storing a
 * single effective `contactId` — the branch when one is chosen, else the company.
 *
 * The key move: when the searched result is a company WITH branches, we do NOT
 * commit it to the form yet. We hold it as a pending selection and reveal the
 * Location field; only picking a location (or the main account) commits a
 * `contactId`. That keeps the host section in its picker state long enough for
 * the two-step flow. A plain contact (or a branch picked straight from search)
 * commits immediately, leaving the residential flow unchanged.
 */
interface CompanyLocationSelectorProps {
  selected: CustomerSelectorSelectedContact | null;
  onSelect: (contact: CustomerSelectorContactResult) => void;
  onClear: () => void;
  placeholder?: string;
  selectedLayout?: 'stacked' | 'inline';
  resultsLayout?: 'dropdown' | 'inline';
  nameOnly?: boolean;
  readOnly?: boolean;
  /** Renders a "New customer" row at the foot of the dropdown. */
  onCreateNew?: () => void;
  /** Controlled search query (forwarded to the inner CustomerSelector). */
  query?: string;
  onQueryChange?: (query: string) => void;
  emptyMessage?: string;
  /**
   * Ask for a branch before committing a company that has some. Off where the
   * host asks for one of the customer's own locations next — the shared
   * customer block on estimates, proposals and jobs. There the branch step put
   * a second "Location" question first, listing child accounts instead of the
   * customer's sites, and held the customer back until it was answered. A
   * branch is still a contact, so searching for it by name commits it.
   */
  pickBranch?: boolean;
}

/** Map a hydrated Contact (a branch) to the picker payload the sections consume. */
function contactToResult(contact: Contact): CustomerSelectorContactResult {
  const primaryContact = getCommercialPrimaryContact(contact);
  return {
    id: contact.id,
    firstName: contact.firstName,
    lastName: contact.lastName,
    title: contact.title,
    displayName: contact.displayName,
    primaryEmail: contact.primaryEmail ?? undefined,
    primaryPhone: contact.primaryPhone ?? undefined,
    primaryContactName: primaryContact?.name,
    primaryContactTitle: primaryContact?.title,
    companyName: contact.companyName ?? undefined,
    contactType: contact.contactType,
    parentContactId: contact.parentContactId ?? undefined,
    childCount: contact.childCount,
    addresses: (contact.addresses ?? []).map((a) => ({
      id: a.id,
      street: a.street ?? a.street1 ?? undefined,
      street2: a.street2,
      city: a.city,
      state: a.state,
      zip: a.zip ?? a.postalCode ?? undefined,
      country: a.country,
      isPrimary: a.isPrimary,
      isService: a.isService,
      isBilling: a.isBilling,
      accessNotes: a.accessNotes ?? undefined,
      type: a.isService ? 'service' : a.isBilling ? 'billing' : undefined,
    })),
  };
}

function resultDisplayName(c: CustomerSelectorContactResult): string {
  return (
    c.displayName?.trim() ||
    `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() ||
    c.companyName?.trim() ||
    'Company'
  );
}

function resultCompanyLabel(c: CustomerSelectorContactResult): string {
  return c.companyName?.trim() || resultDisplayName(c);
}

function branchAddressLine(contact: Contact): string {
  return formatContactAddress(getPrimaryContactAddress(contact.addresses));
}

export function CompanyLocationSelector({
  selected,
  onSelect,
  onClear,
  placeholder = 'Search by name, email, or phone...',
  selectedLayout = 'stacked',
  resultsLayout = 'dropdown',
  nameOnly = false,
  readOnly = false,
  query,
  onQueryChange,
  emptyMessage,
  onCreateNew,
  pickBranch = true,
}: CompanyLocationSelectorProps) {
  // A company the user tapped that has branches, awaiting a Location choice.
  // Held locally so the host section doesn't collapse before the second step.
  const [pendingCompany, setPendingCompany] = useState<CustomerSelectorContactResult | null>(null);

  // Committed selection (or edit mode) → the form owns a contact; delegate to the
  // plain selector's card. Also clear any stale pending state.
  useEffect(() => {
    if (selected) setPendingCompany(null);
  }, [selected]);

  function handleSearchSelect(result: CustomerSelectorContactResult) {
    const primaryContact = getCommercialPrimaryContact(result);
    const enrichedResult = primaryContact
      ? {
          ...result,
          primaryContactName: primaryContact.name,
          primaryContactTitle: primaryContact.title,
        }
      : result;
    // Company with branches → hold for the Location step. Everything else
    // (a branch picked directly, or a plain contact) commits immediately.
    if (pickBranch && needsLocationSelection(result)) {
      setPendingCompany(enrichedResult);
      return;
    }
    onSelect(enrichedResult);
  }

  // The branch-relationship query lives in the child so the plain search path
  // (the common case, and every host that renders this without a QueryClient)
  // never mounts a react-query hook — keeping true drop-in parity with
  // CustomerSelector.
  if (!selected && pendingCompany) {
    return (
      <PendingCompanyPanel
        company={pendingCompany}
        readOnly={readOnly}
        onChangeCompany={() => setPendingCompany(null)}
        onSelect={onSelect}
      />
    );
  }

  return (
    <CustomerSelector
      selected={selected}
      onSelect={handleSearchSelect}
      onClear={onClear}
      placeholder={placeholder}
      selectedLayout={selectedLayout}
      resultsLayout={resultsLayout}
      nameOnly={nameOnly}
      readOnly={readOnly}
      query={query}
      onQueryChange={onQueryChange}
      emptyMessage={emptyMessage}
      onCreateNew={onCreateNew}
    />
  );
}

interface PendingCompanyPanelProps {
  company: CustomerSelectorContactResult;
  readOnly: boolean;
  onChangeCompany: () => void;
  onSelect: (contact: CustomerSelectorContactResult) => void;
}

function PendingCompanyPanel({
  company,
  readOnly,
  onChangeCompany,
  onSelect,
}: PendingCompanyPanelProps) {
  const companyName = resultCompanyLabel(company);
  const [locationFilter, setLocationFilter] = useState('');
  const deferredLocationFilter = useDeferredValue(locationFilter.trim());
  const relationships = useContactChildren(company.id ?? '', deferredLocationFilter);
  const branches = useMemo(
    () => relationships.data?.pages.flatMap((page) => page.children) ?? [],
    [relationships.data],
  );
  // Trust the resolved branch list once it loads; until then (loading or error)
  // fall back to the search result's childCount so the count never reads 0 for a
  // company we already know has branches.
  const locationCount = company.childCount ?? relationships.data?.pages[0]?.total ?? 0;

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Company</p>
        <div className="flex items-center gap-3 rounded-md border bg-card px-3 py-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Building2 className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{companyName}</p>
            <p className="text-xs text-muted-foreground">
              {relationships.isError
                ? "Couldn't load locations"
                : `${locationCount} ${locationCount === 1 ? 'location' : 'locations'}`}
            </p>
          </div>
          {!readOnly && (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="shrink-0 text-xs"
              onClick={onChangeCompany}
            >
              Change
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Location
        </p>
        <LocationPicker
          company={company}
          companyName={companyName}
          branches={branches}
          branchesLoading={relationships.isLoading}
          branchesLoadingMore={relationships.isFetchingNextPage}
          branchesError={relationships.isError}
          hasMoreBranches={relationships.hasNextPage}
          filter={locationFilter}
          onFilterChange={setLocationFilter}
          showFilter={locationCount > 6 || Boolean(locationFilter)}
          onLoadMore={() => void relationships.fetchNextPage()}
          onRetry={() => void relationships.refetch()}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
}

interface LocationPickerProps {
  company: CustomerSelectorContactResult;
  companyName: string;
  branches: Contact[];
  branchesLoading: boolean;
  branchesLoadingMore: boolean;
  branchesError: boolean;
  hasMoreBranches: boolean;
  filter: string;
  onFilterChange: (filter: string) => void;
  showFilter: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  onSelect: (contact: CustomerSelectorContactResult) => void;
}

function LocationPicker({
  company,
  companyName,
  branches,
  branchesLoading,
  branchesLoadingMore,
  branchesError,
  hasMoreBranches,
  filter,
  onFilterChange,
  showFilter,
  onLoadMore,
  onRetry,
  onSelect,
}: LocationPickerProps) {
  const [open, setOpen] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredBranches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return branches;
    return branches.filter((c) => {
      const name = getContactDisplayName(c).toLowerCase();
      return name.includes(q) || branchAddressLine(c).toLowerCase().includes(q);
    });
  }, [branches, filter]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md border bg-card px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent"
      >
        <MapPin className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-muted-foreground">Select a location…</span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-lg">
          {showFilter && (
            <div className="relative border-b p-2">
              <Search
                size={14}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                autoFocus
                value={filter}
                onChange={(e) => onFilterChange(e.target.value)}
                placeholder="Filter locations..."
                className="h-8 pl-7"
              />
            </div>
          )}
          <div className="max-h-56 overflow-y-auto py-1">
            {/* Main account (matriz) — bill the company itself, no branch. */}
            <LocationRow
              icon={<Building2 className="size-3.5" />}
              title="Main account"
              subtitle={companyName}
              onClick={() => onSelect(company)}
            />
            <div className="my-1 border-t" />
            {branchesError ? (
              // A failed branch load must not read as "no locations" — the
              // company is known to have branches (childCount), so offer a retry.
              <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground">
                <span>Couldn't load locations.</span>
                <button
                  type="button"
                  className="font-medium text-primary hover:underline"
                  onClick={onRetry}
                >
                  Retry
                </button>
              </div>
            ) : branchesLoading && branches.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">Loading locations…</p>
            ) : filteredBranches.length === 0 ? (
              <p className="px-3 py-2 text-xs text-muted-foreground">No locations match</p>
            ) : (
              filteredBranches.map((branch) => {
                const address = branchAddressLine(branch);
                return (
                  <LocationRow
                    key={branch.id}
                    icon={<MapPin className="size-3.5" />}
                    title={getContactDisplayName(branch) || 'Location'}
                    subtitle={address || undefined}
                    onClick={() => onSelect(contactToResult(branch))}
                  />
                );
              })
            )}
            {!branchesError && hasMoreBranches ? (
              <button
                type="button"
                className="w-full px-3 py-2 text-center text-xs font-medium text-primary hover:bg-accent disabled:opacity-60"
                disabled={branchesLoadingMore}
                onClick={onLoadMore}
              >
                {branchesLoadingMore ? 'Loading more locations…' : 'Load more locations'}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

interface LocationRowProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
}

function LocationRow({ icon, title, subtitle, onClick }: LocationRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
    >
      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </button>
  );
}
